import React from 'react';
import {Link, Redirect, Route, Switch} from 'react-router-dom';
import Async from 'react-code-splitting';
import {DEFAULT_REDIRECT} from './services/auth/constants';
import Login from './pages/Login';
import Logout from './pages/Logout';
import Signup from './pages/Signup';
import RecoverPassword from './pages/RecoverPassword';
import ResetPassword from './pages/ResetPassword';
import ConsentPage from './pages/Consent';
import FormSuccessPage from './pages/FormSuccessPage';
import InvalidLink from './pages/InvalidLink';
import ServerError from './pages/ServerError';
import Impersonation from './pages/Impersonation';
import {requireAuthentication} from './services/auth/RequireAuthentication';
import {CONSENT_TYPES} from './services/consent/consentTypes';

// Async loading: https://hackernoon.com/straightforward-code-splitting-with-react-and-webpack-4b94c28f6c3f
const PublicJobPosition = () => <Async load={import('./pages/PublicJobPosition')}/>;
const Form = () => <Async load={import('./pages/Form')}/>;
const LogicTest = () => <Async load={import('./pages/LogicTest')}/>;
const LogicTestIrt = () => <Async load={import('./pages/LogicTestIrt')}/>;
const GraphiQL = () => <Async load={import('./pages/GraphiQL')}/>;
const Organization = () => <Async load={import('./pages/Organization')}/>;
const PersonalityTestIrt = () => <Async load={import('./pages/PersonalityTestIrt')}/>;
const User = () => <Async load={import('./pages/User')}/>;

// Wrap pages that require Auth
const WrappedLogicTest = requireAuthentication(LogicTest);
const WrappedLogicTestIrt = requireAuthentication(LogicTestIrt);
const WrappedGraphiQL = requireAuthentication(GraphiQL, {requireSuperUser: true});
const WrappedOrganization = requireAuthentication(Organization);
const WrappedForm = requireAuthentication(Form, {requireSuperUser: true});
const WrappedPersonalityTestIrt = requireAuthentication(PersonalityTestIrt);
const WrappedUser = requireAuthentication(User);

/**
 * 404-page
 */
export const NotFound = () => {
    return (
        <section className="hero is-fullheight alva-bg">
            <div className="hero-body">
                <div className="container">
                    <h1>404</h1>
                    <p>This page does not exist.</p>
                    <Link to="/o">Go to home</Link>
                </div>
            </div>
        </section>
    );
};

/**
 * THE APP
 * @constructor
 */
const App = () => {
    return [
        <div id='full-screen-modal' key={'full-screen-modal'}/>,
        <Switch key={'routes'}>
            <Route path="/o" component={WrappedOrganization}/>
            <Route path="/start" component={WrappedUser}/>
            <Route path="/graphql" component={WrappedGraphiQL}/>
            <Route path="/apply" component={PublicJobPosition}/>
            <Route exact path="/login" component={Login}/>
            <Route exact path="/logout" component={Logout}/>
            <Route exact path="/login/recover" component={RecoverPassword}/>
            <Route exact path="/signup" component={Signup}/>
            <Route exact path="/reset-password" component={ResetPassword}/>
            <Route exact path="/form-success" component={FormSuccessPage}/>
            <Route exact path="/terms" render={() => <ConsentPage type={CONSENT_TYPES.TERMS_OF_SERVICE}/>}/>
            <Route exact path="/improve-alva" render={() => <ConsentPage type={CONSENT_TYPES.IMPROVE_ALVA}/>}/>
            <Route exact path="/share-data" render={() => <ConsentPage type={CONSENT_TYPES.SHARE_DATA_OUTSIDE_EU}/>}/>
            <Route exact path="/personal-data" render={() => <ConsentPage type={CONSENT_TYPES.HANDLE_PERSONAL_DATA}/>}/>
            <Route exact path="/" component={() => <Redirect to={DEFAULT_REDIRECT}/>}/>
            <Route exact path="/form" component={WrappedForm}/>
            <Route exact path="/logic-test" component={WrappedLogicTest}/>
            <Route path="/logic-test-irt" component={WrappedLogicTestIrt}/>
            <Route path="/personality-test-irt" component={WrappedPersonalityTestIrt}/>
            <Route exact path="/invalid-link" component={InvalidLink}/>
            <Route exact path="/error" component={ServerError}/>
            <Route exact path="/impersonation/:token" component={Impersonation}/>
            <Route path='/home' render={(props) => (
                <Redirect to={props.location.pathname.replace('/home', '/o')}/>
            )}/>
            <Route path="*" component={NotFound}/>
        </Switch>
    ];
};


export default App;
